import React, { Fragment, useState, useEffect } from 'react'

import {
  AlignItems,
  Box,
  Display,
  DisplayPosition,
  Heading1,
  JustifyContent,
  Space,
  TextAlign,
  ZIndex,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'
import { useDispatch, useSelector } from 'react-redux'

import { getAvailableBoxSizes } from 'actions/basket'
import { menuLoadBoxPrices } from 'actions/menu'
import { trackUTMAndPromoCode } from 'actions/tracking'
import { boxPricesClickTab } from 'actions/trackingKeys'
import { boxSizes as boxSize, boxTypes, getHeroDetails } from 'routes/BoxPrices/boxPricesConfig'
import { getAvailableNumPortions } from 'selectors/basket'
import { getIsOneThreeFivePortionsEnabled } from 'selectors/features'

import { BoxPricesContent } from './BoxPricesContent'
import { BoxPricesList } from './BoxPricesList'
import { BoxPricesTabs } from './BoxPricesTabs'
import { BoxPricesWithAllPortions } from './BoxPricesWithAllPortions/BoxPricesWithAllPortions'
import { BoxTypesValue, NumPersonsToBoxDescriptors } from './boxPricesModels'
import {
  getNumPersonsToBoxDescriptors,
  getMenuBoxPricesRecieve,
  getError,
} from './boxPricesSelectors'

const BoxPricesComponent = () => {
  const dispatch = useDispatch()

  const loading = useSelector(getMenuBoxPricesRecieve)
  const error = useSelector(getError)
  const isOneThreeFivePortionsEnabled = useSelector(getIsOneThreeFivePortionsEnabled)
  const availableBoxSizes = useSelector(getAvailableNumPortions)

  const numPersonsToBoxDescriptors = useSelector(
    getNumPersonsToBoxDescriptors,
  ) as NumPersonsToBoxDescriptors

  const dispatchTrackUTMAndPromoCode = (
    boxPricesClickTabArg: string,
    boxSizeTrackingValue: string,
  ) =>
    dispatch(
      trackUTMAndPromoCode(boxPricesClickTabArg, {
        box_size: boxSizeTrackingValue,
      }),
    )

  const [activeIndex, setActiveIndex] = useState(0)
  const [activeNumPortion, setActiveNumPortion] = useState(2)
  const [activeRecipes, setActiveRecipes] = useState(boxSize[2].length - 1)

  const boxTypeConfigsArray: BoxTypesValue[] = Object.values(boxTypes)
  const labels: string[] = boxTypeConfigsArray.map(({ title }) => title)

  const handleSetActiveTabIndex = (index: number) => {
    const { boxSizeTrackingValue } = boxTypeConfigsArray[index]
    dispatchTrackUTMAndPromoCode(boxPricesClickTab, boxSizeTrackingValue)
    setActiveIndex(index)
  }

  const handleActivePortionSize = (portionSize: number) => {
    setActiveNumPortion(portionSize)
    setActiveIndex(portionSize)
    setActiveRecipes(boxSize[portionSize].length - 1)
  }

  useEffect(() => {
    if (!availableBoxSizes?.length && isOneThreeFivePortionsEnabled) {
      dispatch(getAvailableBoxSizes())
    }
    dispatch(menuLoadBoxPrices())
  }, [dispatch, availableBoxSizes?.length, isOneThreeFivePortionsEnabled])
  const conditionalLoadingStyle = loading ? { filter: 'blur(5px)' } : {}

  return (
    <Fragment>
      {loading && (
        <Box
          display={Display.Flex}
          position={DisplayPosition.Fixed}
          top="0px"
          zIndex={ZIndex.Modal}
          width="100%"
          height="100%"
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          data-testid="boxPricesComponentLoading"
        >
          <Loading />
        </Box>
      )}
      <Box style={conditionalLoadingStyle} data-testid="boxPricesComponentLoaded">
        <Space size={[6, 8]} direction="vertical" />
        <Heading1 textAlign={TextAlign.Center}>{getHeroDetails().header}</Heading1>
        <Space size={[6, 8]} direction="vertical" />
        {!loading && !isOneThreeFivePortionsEnabled && (
          <Fragment>
            <BoxPricesTabs
              activeIndex={activeIndex}
              labels={labels}
              setActiveIndex={handleSetActiveTabIndex}
            />
            <BoxPricesList
              numPersonsToBoxDescriptors={numPersonsToBoxDescriptors}
              selectedBox={boxTypeConfigsArray[activeIndex].value}
              error={error}
            />
          </Fragment>
        )}
        {!loading && isOneThreeFivePortionsEnabled && (
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            gap={8}
            data-testid="boxPricesWithAllPortions"
          >
            {availableBoxSizes && (
              <BoxPricesWithAllPortions
                boxInfo={numPersonsToBoxDescriptors}
                onClickPortionSize={handleActivePortionSize}
                activeNumPortion={activeNumPortion}
                portionSizes={availableBoxSizes}
                activeRecipes={activeRecipes}
                setActiveRecipes={setActiveRecipes}
              />
            )}
          </Box>
        )}
        <Space size={[0, 18]} direction="vertical" />
        <BoxPricesContent />
      </Box>
    </Fragment>
  )
}

export { BoxPricesComponent }
